"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Play, Plus } from "lucide-react";
import type { BlogPost, Media } from "@/payload-types";
import { getFocalPosition } from "@/lib/media-utils";
import { siteConfig } from "@/lib/site.config";
import { cn } from "@/lib/utils";
import { GridSectionCrosshairs } from "@/payload/blocks/grid-sections/GridSectionCrosshairs";

const LINE_CSS_COLOR = "color-mix(in oklab, white 15%, transparent)";

interface Props {
  heading?: string;
  posts: BlogPost[];
  lineColor: string;
  hashLink?: string;
  isFirstGrid?: boolean;
}

export function FeaturedArticlesCarouselClient({
  heading,
  posts,
  lineColor,
  hashLink,
  isFirstGrid = true,
}: Props) {
  const [index, setIndex] = useState(0);
  const total = posts.length;
  const post = posts[index];

  const image =
    post.featuredImage && typeof post.featuredImage === "object"
      ? (post.featuredImage as Media)
      : null;

  const date = post.publishedDate
    ? new Date(post.publishedDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  const href = `${siteConfig.blogPath}/${post.slug}`;

  const goPrev = () => setIndex((i) => (i - 1 + total) % total);
  const goNext = () => setIndex((i) => (i + 1) % total);

  const textCellClass = "col-span-1 aspect-square 2xl:aspect-[4/3]";
  const imageCellClass =
    "col-span-1 aspect-square lg:col-span-2 lg:aspect-[2/1] 2xl:aspect-[8/3]";

  return (
    <section
      id={hashLink}
      className={cn(
        "relative w-full bg-black text-white grid-decorative-verticals",
        `border-x ${lineColor}`,
        isFirstGrid && `border-t ${lineColor}`,
        `border-b ${lineColor}`
      )}
      style={{ ["--grid-line-color" as string]: LINE_CSS_COLOR }}
    >
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 px-4 sm:px-6 lg:px-0">
        {/* Left — eyebrow + content + nav arrows */}
        <div className={cn("decorative-grid-cell grid-cell lg:border-t-0", textCellClass)}>
          <div className="flex flex-col h-full p-3 sm:p-4 lg:p-5">
            {heading && (
              <p className="text-xs text-white/60">{heading}</p>
            )}

            <div className="flex-1 flex flex-col justify-center max-w-[26rem]">
              <h3 className="font-subheading text-2xl lg:text-3xl 2xl:text-4xl text-white mb-2 leading-tight">
                {post.title}
              </h3>
              {date && <p className="text-xs text-white/60 mb-4">{date}</p>}
              {post.excerpt && (
                <p className="text-sm text-white/80 mb-5 line-clamp-4">
                  {post.excerpt}
                </p>
              )}
              <Link
                href={href}
                className="inline-flex items-center gap-2 text-sm text-white border border-white/30 px-3 py-1.5 hover:border-white/60 transition-colors w-fit"
              >
                Read More
                <Plus className="h-3.5 w-3.5" />
              </Link>
            </div>

            {total > 1 && (
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous featured article"
                  className="inline-flex items-center justify-center h-9 w-9 border border-white/30 text-white/70 hover:border-white/60 hover:text-white transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next featured article"
                  className="inline-flex items-center justify-center h-9 w-9 border border-white/30 text-white/70 hover:border-white/60 hover:text-white transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right — image with play overlay */}
        <div className={cn("decorative-grid-cell grid-cell lg:border-t-0", imageCellClass)}>
          <div className="h-full p-3 sm:p-4 lg:p-5">
            <Link
              href={href}
              className="group relative block h-full overflow-hidden"
              aria-label={post.title}
            >
              {image?.url && (
                <Image
                  src={image.url}
                  alt={image.alt || post.title}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  style={{ objectPosition: getFocalPosition(image) }}
                  sizes="(max-width: 1024px) 100vw, 66vw"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-black/40" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex items-center justify-center h-14 w-14 lg:h-16 lg:w-16 border border-white/70 text-white/90 backdrop-blur-[2px] bg-black/10 group-hover:bg-black/20 transition-colors">
                  <Play className="h-5 w-5 lg:h-6 lg:w-6" fill="currentColor" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>
      <GridSectionCrosshairs />
    </section>
  );
}
